import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  AdminAnecdote,
  AdminAttachmentName,
  AdminAuditLogsResponse,
  AdminBookDetail,
  AdminBookUpdateParams,
  AdminContentRecord,
  AdminContentResource,
  AdminDashboardStats,
  AdminGeneratedChapter,
  AdminInfra,
  AdminJobs,
  AdminJobSet,
  AdminSearchResult,
  AdminSettings,
  AdminUserCreateParams,
  AdminPrintOrder,
  AdminPrintOrdersResponse,
  AdminPrinterApiStatus,
  AdminPrintOrderUpdateParams,
  AdminSubscriptionUpdateParams,
  AdminUserDetail,
  AdminUsersFilters,
  AdminUsersResponse,
  AdminUserUpdateParams,
  AdminSubscription,
  AdminSubscriptionsFilters,
  AdminSubscriptionsResponse,
  PageParams,
  SubscriptionTargetParams,
} from "../types/admin";
import { memoirsApiInstance } from "./api";

const adminKeys = {
  dashboard: ["admin", "dashboard"] as const,
  users: (filters?: AdminUsersFilters) =>
    ["admin", "users", filters ?? {}] as const,
  user: (id?: number) => ["admin", "user", id] as const,
  printOrders: (status?: string, page?: PageParams) =>
    ["admin", "print_orders", status ?? "all", page ?? {}] as const,
  printOrder: (id?: number) => ["admin", "print_order", id] as const,
  book: (id?: number) => ["admin", "book", id] as const,
  infra: ["admin", "infra"] as const,
  jobs: (set?: AdminJobSet, page?: PageParams) =>
    ["admin", "jobs", set ?? "retry", page ?? {}] as const,
  subscriptions: (filters?: AdminSubscriptionsFilters) =>
    ["admin", "subscriptions", filters ?? {}] as const,
  content: (resource: AdminContentResource) =>
    ["admin", "content", resource] as const,
  search: (q: string) => ["admin", "search", q] as const,
  auditLogs: (page?: PageParams) =>
    ["admin", "audit_logs", page ?? {}] as const,
  settings: ["admin", "settings"] as const,
};

// DASHBOARD ==================================================================
export const useAdminDashboard = () =>
  useQuery(adminKeys.dashboard, () =>
    memoirsApiInstance.get<AdminDashboardStats>("/admin/dashboard"),
  );

// USERS ======================================================================
export const useAdminUsers = (filters?: AdminUsersFilters) =>
  useQuery(
    adminKeys.users(filters),
    () =>
      memoirsApiInstance.get<AdminUsersResponse>("/admin/users", {
        params: filters,
      }),
    { keepPreviousData: true },
  );

export const useAdminUser = (id?: number) =>
  useQuery(
    adminKeys.user(id),
    () => memoirsApiInstance.get<AdminUserDetail>(`/admin/users/${id}`),
    { enabled: !!id },
  );

export const useUpdateAdminUser = (id: number) => {
  const client = useQueryClient();
  return useMutation(
    (user: AdminUserUpdateParams) =>
      memoirsApiInstance.patch<AdminUserDetail>(`/admin/users/${id}`, { user }),
    {
      onSuccess: (data) => {
        client.setQueryData(adminKeys.user(id), data);
        client.invalidateQueries(["admin", "users"]);
      },
    },
  );
};

export const useCreateAdminUser = () => {
  const client = useQueryClient();
  return useMutation(
    (params: AdminUserCreateParams) =>
      memoirsApiInstance.post<AdminUserDetail>("/admin/users", params),
    { onSuccess: () => client.invalidateQueries(["admin", "users"]) },
  );
};

export const useDeleteAdminUser = () => {
  const client = useQueryClient();
  return useMutation(
    (id: number) => memoirsApiInstance.delete(`/admin/users/${id}`),
    {
      onSuccess: () => {
        client.invalidateQueries(["admin", "users"]);
        client.invalidateQueries(adminKeys.dashboard);
      },
    },
  );
};

const useAdminUserAction = (
  id: number,
  action:
    | "resend_confirmation"
    | "resend_invitation"
    | "send_reset_password"
    | "confirm"
    | "unlock",
) => {
  const client = useQueryClient();
  return useMutation(
    () => memoirsApiInstance.post(`/admin/users/${id}/${action}`),
    {
      onSuccess: () => client.invalidateQueries(adminKeys.user(id)),
    },
  );
};

export const useResendConfirmation = (id: number) =>
  useAdminUserAction(id, "resend_confirmation");
export const useUnlockUser = (id: number) => useAdminUserAction(id, "unlock");
export const useResendInvitation = (id: number) =>
  useAdminUserAction(id, "resend_invitation");
export const useSendResetPassword = (id: number) =>
  useAdminUserAction(id, "send_reset_password");
export const useConfirmUser = (id: number) => useAdminUserAction(id, "confirm");

// SUBSCRIPTIONS ==============================================================
export const useUpdateSubscription = (userId: number) => {
  const client = useQueryClient();
  return useMutation(
    ({ id, params }: { id: number; params: AdminSubscriptionUpdateParams }) =>
      memoirsApiInstance.patch(`/admin/subscriptions/${id}`, {
        subscription: params,
      }),
    {
      onSuccess: () => {
        client.invalidateQueries(adminKeys.user(userId));
        client.invalidateQueries(["admin", "subscriptions"]);
      },
    },
  );
};

export const useAdminSubscriptions = (filters?: AdminSubscriptionsFilters) =>
  useQuery(
    adminKeys.subscriptions(filters),
    () =>
      memoirsApiInstance.get<AdminSubscriptionsResponse>(
        "/admin/subscriptions",
        { params: filters },
      ),
    { keepPreviousData: true },
  );

/** Moving a subscription touches two users, the lists and the dashboard. */
const useSubscriptionTransfer = <TResult>(action: "reassign" | "split") => {
  const client = useQueryClient();
  return useMutation(
    ({ id, params }: { id: number; params: SubscriptionTargetParams }) =>
      memoirsApiInstance.post<TResult>(
        `/admin/subscriptions/${id}/${action}`,
        params,
      ),
    {
      onSuccess: () => {
        client.invalidateQueries(["admin", "user"]);
        client.invalidateQueries(["admin", "users"]);
        client.invalidateQueries(["admin", "subscriptions"]);
        client.invalidateQueries(adminKeys.dashboard);
      },
    },
  );
};

export const useReassignSubscription = () =>
  useSubscriptionTransfer<AdminSubscription>("reassign");

export const useSplitSubscription = () =>
  useSubscriptionTransfer<{
    original: AdminSubscription;
    created: AdminSubscription;
  }>("split");

// PRINT ORDERS ===============================================================
export const useAdminPrintOrders = (status?: string, page?: PageParams) =>
  useQuery(
    adminKeys.printOrders(status, page),
    () =>
      memoirsApiInstance.get<AdminPrintOrdersResponse>("/admin/print_orders", {
        params: { ...page, status: status || undefined },
      }),
    { keepPreviousData: true },
  );

export const useUpdatePrintOrder = () => {
  const client = useQueryClient();
  return useMutation(
    ({ id, params }: { id: number; params: AdminPrintOrderUpdateParams }) =>
      memoirsApiInstance.patch<AdminPrintOrder>(`/admin/print_orders/${id}`, {
        print_order: params,
      }),
    {
      onSuccess: () => {
        client.invalidateQueries(["admin", "print_orders"]);
        client.invalidateQueries(["admin", "user"]);
      },
    },
  );
};

export const useDeletePrintOrder = () => {
  const client = useQueryClient();
  return useMutation(
    (id: number) => memoirsApiInstance.delete(`/admin/print_orders/${id}`),
    {
      onSuccess: () => {
        client.invalidateQueries(["admin", "print_orders"]);
        client.invalidateQueries(["admin", "user"]);
      },
    },
  );
};

export const downloadPrintOrdersCsv = async (status?: string) => {
  const blob = await memoirsApiInstance.get<Blob>(
    "/admin/print_orders/export",
    { params: status ? { status } : undefined, responseType: "blob" },
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `impressions-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export const useConfirmPrintOrder = () => {
  const client = useQueryClient();
  return useMutation(
    (id: number) =>
      memoirsApiInstance.post<AdminPrintOrder>(
        `/admin/print_orders/${id}/confirm`,
      ),
    {
      onSuccess: () => {
        client.invalidateQueries(["admin", "print_orders"]);
        client.invalidateQueries(["admin", "user"]);
      },
    },
  );
};

export const useAdminPrinterApiStatus = () =>
  useQuery(["admin", "printer_api"], () =>
    memoirsApiInstance.get<AdminPrinterApiStatus>(
      "/admin/print_orders/printer",
    ),
  );

const usePrintOrderAction = (
  action: "send_to_printer" | "refresh_tracking",
) => {
  const client = useQueryClient();
  return useMutation(
    (id: number) =>
      memoirsApiInstance.post<AdminPrintOrder>(
        `/admin/print_orders/${id}/${action}`,
      ),
    {
      onSettled: () => {
        client.invalidateQueries(["admin", "print_orders"]);
        client.invalidateQueries(["admin", "user"]);
      },
    },
  );
};

export const useSendPrintOrderToPrinter = () =>
  usePrintOrderAction("send_to_printer");

export const useRefreshPrintOrderTracking = () =>
  usePrintOrderAction("refresh_tracking");

// BOOKS ======================================================================
export const useAdminBook = (id?: number) =>
  useQuery(
    adminKeys.book(id),
    () => memoirsApiInstance.get<AdminBookDetail>(`/admin/books/${id}`),
    { enabled: !!id },
  );

/** Every book endpoint answers with the full book payload: store it as is. */
const useBookMutation = <TVariables>(
  id: number,
  request: (variables: TVariables) => Promise<AdminBookDetail>,
) => {
  const client = useQueryClient();
  return useMutation(request, {
    onSuccess: (data) => {
      client.setQueryData(adminKeys.book(id), data);
      client.invalidateQueries(["admin", "user"]);
      client.invalidateQueries(adminKeys.dashboard);
    },
  });
};

export const useUpdateAdminBook = (id: number) =>
  useBookMutation(id, (book: AdminBookUpdateParams) =>
    memoirsApiInstance.patch<AdminBookDetail>(`/admin/books/${id}`, { book }),
  );

export const useGenerateBookAi = (id: number) =>
  useBookMutation(id, (params: { model?: string; effort?: string }) =>
    memoirsApiInstance.post<AdminBookDetail>(
      `/admin/books/${id}/generate_ai`,
      params,
    ),
  );

export const useBookAction = (
  id: number,
  action: "generate_audio" | "validate_audio" | "generate_zip" | "retrieve_ai",
) =>
  useBookMutation(id, () =>
    memoirsApiInstance.post<AdminBookDetail>(`/admin/books/${id}/${action}`),
  );

export const useGenerateBookPdf = (id: number) =>
  useBookMutation(id, (size?: string) =>
    memoirsApiInstance.post<AdminBookDetail>(
      `/admin/books/${id}/generate_pdf`,
      { size },
    ),
  );

export const usePurgeBookAttachment = (id: number) =>
  useBookMutation(id, (name: AdminAttachmentName) =>
    memoirsApiInstance.delete<AdminBookDetail>(
      `/admin/books/${id}/attachments/${name}`,
    ),
  );

/** Mutations on records nested in a book refetch the whole book page. */
const useBookChildMutation = <TVariables, TResult>(
  bookId: number,
  request: (variables: TVariables) => Promise<TResult>,
) => {
  const client = useQueryClient();
  return useMutation(request, {
    onSuccess: () => client.invalidateQueries(adminKeys.book(bookId)),
  });
};

export const useUpdateGeneratedChapter = (bookId: number) =>
  useBookChildMutation(
    bookId,
    ({
      id,
      params,
    }: {
      id: number;
      params: Partial<
        Pick<AdminGeneratedChapter, "title" | "content" | "completed">
      >;
    }) =>
      memoirsApiInstance.patch<AdminGeneratedChapter>(
        `/admin/generated_chapters/${id}`,
        { generated_chapter: params },
      ),
  );

export const useResetChapterAudio = (bookId: number) =>
  useBookChildMutation(bookId, (id: number) =>
    memoirsApiInstance.delete(`/admin/generated_chapters/${id}/audio`),
  );

export const useUpdateAnswer = (bookId: number) =>
  useBookChildMutation(
    bookId,
    ({
      id,
      params,
    }: {
      id: number;
      params: { content?: string; completed?: boolean };
    }) => memoirsApiInstance.patch(`/admin/answers/${id}`, { answer: params }),
  );

export const useUpdateAnecdote = (bookId: number) =>
  useBookChildMutation(
    bookId,
    ({
      id,
      params,
    }: {
      id: number;
      params: Partial<
        Pick<
          AdminAnecdote,
          "status" | "content" | "expires_at" | "custom_message"
        >
      >;
    }) =>
      memoirsApiInstance.patch<AdminAnecdote>(`/admin/anecdotes/${id}`, {
        anecdote: params,
      }),
  );

export const useResendAnecdote = (bookId: number) =>
  useBookChildMutation(bookId, (id: number) =>
    memoirsApiInstance.post(`/admin/anecdotes/${id}/resend`),
  );

export const useDeleteAnecdote = (bookId: number) =>
  useBookChildMutation(bookId, (id: number) =>
    memoirsApiInstance.delete(`/admin/anecdotes/${id}`),
  );

export const useDeleteMedium = (bookId: number) =>
  useBookChildMutation(bookId, (id: number) =>
    memoirsApiInstance.delete(`/admin/media/${id}`),
  );

// INFRA ======================================================================
export const useAdminInfra = () =>
  useQuery(
    adminKeys.infra,
    () => memoirsApiInstance.get<AdminInfra>("/admin/infra"),
    { refetchInterval: 15000 },
  );

const useInfraMutation = <TVariables>(
  request: (variables: TVariables) => Promise<AdminInfra>,
) => {
  const client = useQueryClient();
  return useMutation(request, {
    onSuccess: (data) => {
      client.setQueryData(adminKeys.infra, data);
    },
  });
};

export const useResizeProcess = () =>
  useInfraMutation(({ process, size }: { process: string; size: string }) =>
    memoirsApiInstance.patch<AdminInfra>(`/admin/infra/formation/${process}`, {
      size,
    }),
  );

export const useDynoAction = (action: "restart" | "stop") =>
  useInfraMutation((name: string) =>
    memoirsApiInstance.post<AdminInfra>(`/admin/infra/dynos/${name}/${action}`),
  );

// JOBS =======================================================================
export const useAdminJobs = (set: AdminJobSet, page?: PageParams) =>
  useQuery(
    adminKeys.jobs(set, page),
    () =>
      memoirsApiInstance.get<AdminJobs>("/admin/jobs", {
        params: { ...page, set },
      }),
    { refetchInterval: 10000, keepPreviousData: true },
  );

const useJobMutation = <TVariables>(
  request: (variables: TVariables) => Promise<unknown>,
) => {
  const client = useQueryClient();
  return useMutation(request, {
    onSuccess: () => client.invalidateQueries(["admin", "jobs"]),
  });
};

export const useRetryJob = () =>
  useJobMutation(({ set, jid }: { set: AdminJobSet; jid: string }) =>
    memoirsApiInstance.post(`/admin/jobs/${set}/${jid}/retry`),
  );

export const useDeleteJob = () =>
  useJobMutation(({ set, jid }: { set: AdminJobSet; jid: string }) =>
    memoirsApiInstance.delete(`/admin/jobs/${set}/${jid}`),
  );

export const useClearJobSet = () =>
  useJobMutation((set: AdminJobSet) =>
    memoirsApiInstance.delete(`/admin/jobs/${set}`),
  );

// CONTENT ====================================================================
export const useAdminContent = (resource: AdminContentResource) =>
  useQuery(adminKeys.content(resource), () =>
    memoirsApiInstance.get<{ records: AdminContentRecord[] }>(
      `/admin/content/${resource}`,
    ),
  );

export const useSaveContentRecord = (resource: AdminContentResource) => {
  const client = useQueryClient();
  return useMutation(
    ({ id, record }: { id?: number; record: Record<string, unknown> }) =>
      id
        ? memoirsApiInstance.patch(`/admin/content/${resource}/${id}`, {
            record,
          })
        : memoirsApiInstance.post(`/admin/content/${resource}`, { record }),
    { onSuccess: () => client.invalidateQueries(adminKeys.content(resource)) },
  );
};

export const useDeleteContentRecord = (resource: AdminContentResource) => {
  const client = useQueryClient();
  return useMutation(
    (id: number) =>
      memoirsApiInstance.delete(`/admin/content/${resource}/${id}`),
    { onSuccess: () => client.invalidateQueries(adminKeys.content(resource)) },
  );
};

// SEARCH / AUDIT =============================================================
export const useAdminSearch = (q: string) =>
  useQuery(
    adminKeys.search(q),
    () =>
      memoirsApiInstance.get<{ results: AdminSearchResult[] }>(
        "/admin/search",
        { params: { q } },
      ),
    { enabled: q.trim().length >= 2, keepPreviousData: true },
  );

export const useAdminAuditLogs = (page?: PageParams) =>
  useQuery(
    adminKeys.auditLogs(page),
    () =>
      memoirsApiInstance.get<AdminAuditLogsResponse>("/admin/audit_logs", {
        params: page,
      }),
    { keepPreviousData: true },
  );

// SETTINGS ===================================================================
export const useAdminSettings = () =>
  useQuery(adminKeys.settings, () =>
    memoirsApiInstance.get<AdminSettings>("/admin/settings"),
  );

export const useUpdateAdminSettings = () => {
  const client = useQueryClient();
  return useMutation(
    (
      params:
        { claude_model: string; claude_effort: string } | { pdf_size: string },
    ) => memoirsApiInstance.patch<AdminSettings>("/admin/settings", params),
    {
      onSuccess: (data) => {
        client.setQueryData(adminKeys.settings, data);
        client.invalidateQueries(["admin", "book"]);
      },
    },
  );
};
